import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react'
import { useForm } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { Button, TextField } from '@shared/design-system/ui'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { ForgotPasswordInputSchema } from '../api/auth.contract'
import { useForgotPasswordMutation } from '../api/auth.mutations'
import type { ForgotPasswordInput } from '../api/auth.contract'
import { actions, form, link } from './auth.css'
import { AuthLayout } from './AuthLayout'

export function ForgotPasswordPage() {
  const forgotMutation = useForgotPasswordMutation()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordInputSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await forgotMutation.mutateAsync(values)
      toast.success('Enviamos as instruções para seu e-mail.')
    } catch (error) {
      const hasFieldErrors = applyApiFieldErrors(error, (field, fieldError) =>
        setError(field as Path<ForgotPasswordInput>, fieldError),
      )
      if (hasFieldErrors) {
        toast.error({
          title: 'Revise os campos',
          description: 'Informe um e-mail válido para receber as instruções.',
        })
        return
      }

      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível enviar as instruções',
          fallback: 'Confira o e-mail e tente novamente.',
        }),
      )
    }
  })

  return (
    <AuthLayout
      title="Recuperar senha"
      subtitle="Digite seu e-mail para receber o link de redefinição."
    >
      <form className={form} onSubmit={(event) => void onSubmit(event)}>
        <TextField
          label="E-mail"
          type="email"
          autoComplete="email"
          leftIcon={<Mail size={17} />}
          error={errors.email?.message}
          {...register('email')}
        />
        <div className={actions}>
          <Button type="submit" fullWidth disabled={forgotMutation.isPending}>
            {forgotMutation.isPending ? 'Enviando...' : 'Enviar instruções'}
          </Button>
          <a className={link} href="/login">
            Voltar para login
          </a>
        </div>
      </form>
    </AuthLayout>
  )
}
